import React, { useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import Select from 'react-select'
import { ClientService } from '../../component/services/client.service';
import StatsCard from '../../component/Cards/StatsCard';
import StatsCardWithProgress from '../../component/Cards/StatsCardWithProgress';
import DonutChart, { ChartDataItem } from '../../component/Charts/DonutChart';
import CetLoader from '../../component/Loader/CetLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers, 
    faCheckCircle, 
    faTimesCircle, 
    faUniversity, 
    faUserCheck, 
    faUserTie, 
    faPlay, 
    faClipboardCheck 
} from '@fortawesome/free-solid-svg-icons';

export default function CollegeOverviewDev() {
    const [colleges, setColleges] = useState<any[]>([]);
    const [selectedCollege, setSelectedCollege] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [collegeStats, setCollegeStats] = useState<any>(null);
    const [chartData, setChartData] = useState<ChartDataItem[]>([]);

    async function fetchColleges() {
        setLoading(true);
        await ClientService.getReverificationColleges()
            .then((res: any) => {
                if (res.status === 200) {
                    const collegeOptions = res.data?.data?.map((college: any) => ({
                        value: college?.collegId,
                        label: college?.collegeName
                    })) || [];
                    setColleges(collegeOptions);
                } else {
                    console.error("Failed to fetch colleges:", res);
                }
            })
            .catch((error: any) => {
                console.error("Error fetching colleges:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    async function getCollegeStats() {
        setLoading(true);
        await ClientService.getReverificationCollegeStats(selectedCollege)
            .then((res: any) => {
                if (res.status === 200) {
                    const statsData = res.data?.data || null;
                    setCollegeStats(statsData);
                    
                    // Prepare chart data for success/failure visualization
                    if (statsData) {
                        const newChartData: ChartDataItem[] = [
                            {
                                category: "Success",
                                value: statsData.successCount || 0,
                                fill: "#28a745"
                            },
                            {
                                category: "Failure", 
                                value: statsData.failureCount || 0,
                                fill: "#dc3545"
                            }
                        ];
                        setChartData(newChartData);
                    }
                } else {
                    console.error("Failed to fetch college stats:", res);
                }
            })
            .catch((error: any) => {
                console.error("Error fetching college stats:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        if (selectedCollege) {
            getCollegeStats();
        }
    }, [selectedCollege]);


    useEffect(() => {
        fetchColleges();
    }, []);
    return (
        <>
            <Card className="mt-4 mb-5 shadow" style={{ borderRadius: "16px", border: "1px solid #e9ecef" }}>
                <Card.Body className="p-4">

                    <Row className="mt-2 text-left mb-0">
                        <Col md={8}>
                            <h5 className="text-secondary fw-bold mb-3" style={{ fontSize: '18px' }}>
                                College Overview
                            </h5>
                        </Col>
                        <Col md={4}>
                            <Form.Label className="text-muted fw-bold">
                                Select College
                            </Form.Label>
                            <Select
                                options={colleges}
                                placeholder="Select Colleges"
                                onChange={(e: any) => setSelectedCollege(e?.value)}
                                isClearable
                                value={selectedCollege ? colleges.find((college: any) => college.value === selectedCollege.value) : null}
                            />
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="mt-4">
                            <CetLoader />
                        </div>
                    ) : selectedCollege && collegeStats ? (
                        <>
                            {/* College Stats Overview */}
                            <Row className="mt-4">
                                <Col md={6}>
                                    {chartData.length > 0 ? (
                                        <DonutChart
                                            data={chartData}
                                            centerText={{
                                                label: "Total Verifications",
                                                value: collegeStats?.totalCount || 0
                                            }}
                                            height={350}
                                            width={350}
                                            showTooltip={true}
                                            showLegend={true}
                                            showCalloutLabels={true}
                                        />
                                    ) : (
                                        <NoDataView message="No verification data available for this college." />
                                    )}
                                </Col>
                                <Col md={6}>
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <StatsCard
                                                title="Total Verifications"
                                                value={collegeStats?.totalCount || 0}
                                                icon={faClipboardCheck}
                                                iconColor="primary"
                                                valueColor="primary"
                                            />
                                        </Col>
                                        <Col md={12}>
                                            <StatsCard
                                                title="Successful Verifications"
                                                value={collegeStats?.successCount || 0}
                                                icon={faCheckCircle}
                                                iconColor="success"
                                                valueColor="success"
                                            />
                                        </Col>
                                        <Col md={12}>
                                            <StatsCardWithProgress
                                                title="Success Rate"
                                                value={`${((collegeStats?.successCount || 0) / (collegeStats?.totalCount || 1) * 100).toFixed(1)}%`}
                                                progress={(collegeStats?.successCount || 0) / (collegeStats?.totalCount || 1) * 100}
                                                icon={faUserCheck}
                                                iconColor="success"
                                                valueColor="success"
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            {/* Additional College Metrics */}
                            <Row className="mt-4 g-3">
                                <Col lg={3} md={6}>
                                    <StatsCard
                                        title="Failed Verifications"
                                        value={collegeStats?.failureCount || 0}
                                        icon={faTimesCircle}
                                        iconColor="danger"
                                        valueColor="danger"
                                    />
                                </Col>
                                <Col lg={3} md={6}>
                                    <StatsCard
                                        title="Unique Verifications"
                                        value={collegeStats?.uniqueVerifications || 0}
                                        icon={faUsers}
                                        iconColor="warning"
                                        valueColor="warning"
                                    />
                                </Col>
                                <Col lg={3} md={6}>
                                    <StatsCard
                                        title="Total Operators"
                                        value={collegeStats?.totalOperators || 0}
                                        icon={faUserTie}
                                        iconColor="info"
                                        valueColor="info"
                                    />
                                </Col>
                                <Col lg={3} md={6}>
                                    <StatsCard
                                        title="Active Operators"
                                        value={collegeStats?.verificationStartedOperators || 0}
                                        icon={faPlay}
                                        iconColor="success"
                                        valueColor="success"
                                    />
                                </Col>
                            </Row>

                            {/* Operator Activity */}
                            <Card className="mt-4 shadow" style={{ borderRadius: "16px", border: "1px solid #e9ecef" }}>
                                <Card.Body className="p-4">
                                    <h5 className="text-secondary fw-bold mb-3" style={{ fontSize: '18px' }}>
                                        Operator Activity
                                    </h5>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <StatsCardWithProgress
                                                title="Operator Participation Rate"
                                                value={`${((collegeStats?.verificationStartedOperators || 0) / (collegeStats?.totalOperators || 1) * 100).toFixed(1)}%`}
                                                progress={(collegeStats?.verificationStartedOperators || 0) / (collegeStats?.totalOperators || 1) * 100}
                                                icon={faUserTie}
                                                iconColor="info"
                                                valueColor="info"
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <StatsCard
                                                title="Inactive Operators"
                                                value={(collegeStats?.totalOperators || 0) - (collegeStats?.verificationStartedOperators || 0)}
                                                icon={faUserTie}
                                                iconColor="secondary"
                                                valueColor="secondary"
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    ) : selectedCollege ? (
                        <NoDataView message="No data available for the selected college." />
                    ) : (
                        <NoDataView message="Please select a college to view statistics." />
                    )}
                </Card.Body>
            </Card>
        </>
    )
}

const NoDataView = ({ message }: { message: string }) => {
    return (
        <div className="text-center py-5">
            <div className="mb-3">
                <FontAwesomeIcon
                    icon={faUniversity}
                    className="text-muted"
                    style={{ fontSize: '48px', opacity: 0.3 }}
                />
            </div>
            <div className="text-muted" style={{ fontSize: '16px' }}>
                {message || "No college data available."}
            </div>
        </div>
    );
}